import {
  ClickEventArgs,
  Graph,
  LabelStyle,
  EdgePathLabelModel,
  EdgeSides,
  FoldingManager,
  GraphComponent,
  GraphItemTypes,
  GraphViewerInputMode,
  HierarchicalLayout,
  HierarchicalLayoutData,
  HorizontalTextAlignment,
  HoveredItemChangedEventArgs,
  IEdge,
  IEnumerable,
  IFoldingView,
  IGraph,
  ILabel,
  IModelItem,
  INode,
  InputModeItemEventArgs,
  ItemClickedEventArgs,
  ItemHoverInputMode,
  LayoutExecutor,
  LayoutOrientation,
  NavigationInputMode,
  NodeAlignmentPolicy,
  PolylineEdgeStyle,
  EdgeLabelPreferredPlacement,
  Rect,
  RectangleNodeStyle,
  SmartEdgeLabelModel,
  Stroke,
  TextWrapping,
  TimeSpan,
  VerticalTextAlignment,
  Color,
  HierarchicalLayoutRoutingStyle,
  ExteriorNodeLabelModel,
  ExteriorNodeLabelModelPosition,
  InsetsConvertible,
  SizeConvertible,
  Size,
} from '@ardoq/yfiles';
import { AqGroupNodeStyle } from './customYFilesImplementations/AqGroupNodeStyle';
import { AqCollapsibleNodeStyleDecorator } from './customYFilesImplementations/AqCollapsibleNodeStyleDecorator';
import {
  IEdgeTag,
  SimpleBlockDiagramEdge,
  SimpleBlockDiagramNode,
} from './types';
import { ArdoqId, ArrowType } from '@ardoq/api-types';
import { parseToHsl, setLightness, transparentize } from 'polished';
import { AqArrow } from './customYFilesImplementations/AqArrow';
import { RepresentationData } from '@ardoq/data-model';
import {
  CollapseExpandButtonDefaults,
  EDGE_LABEL_CONTAINER_CLASS_NAME,
  EdgeDefaults,
  FontDefaults,
  GroupNodeDefaults,
  NODE_FONT,
  NODE_LABEL_SIZE,
  NodeDefaults,
} from './consts';
import { AqShapeAndIconNodeStyle } from './customYFilesImplementations/AqShapeAndIconNodeStyle';
import { colors } from '@ardoq/design-tokens';
import { IconName } from '@ardoq/icons';
import { ButtonConfig, SettingsType } from '@ardoq/view-settings';
import {
  processDuplicateLabels,
  AggregatedEdgeLabelStyle,
  groupEdges,
} from '@ardoq/graph/yfiles';
import { ZoomToNodesLayoutExecutor } from './customYFilesImplementations/ZoomToNodesLayoutExecutor';

const ANIMATION_DURATION_TIME_SPAN = TimeSpan.fromMilliseconds(500);
const DEFAULT_PLACEMENT_DESCRIPTOR = new EdgeLabelPreferredPlacement();
const countLabelStyle = new AggregatedEdgeLabelStyle();
const edgeCountLabelParameter =
  new SmartEdgeLabelModel().createParameterFromCenter();

// GraphVisualization utils
export const createCollapsibleGraphViewerInputMode = () => {
  const { EDGE, NODE, LABEL } = GraphItemTypes;
  const inputMode = new GraphViewerInputMode({
    focusableItems: (NODE as number) | (EDGE as number),
    selectableItems: (NODE as number) | (EDGE as number) | (LABEL as number),
  });
  inputMode.navigationInputMode.allowCollapseGroup = true;
  inputMode.navigationInputMode.allowExpandGroup = true;
  inputMode.navigationInputMode.fitContentAfterGroupActions = false;
  inputMode.navigationInputMode.autoGroupNodeAlignmentPolicy =
    NodeAlignmentPolicy.CENTER;
  inputMode.itemHoverInputMode.enabled = true;
  inputMode.itemHoverInputMode.hoverItems =
    (NODE as number) | (EDGE as number) | (LABEL as number);
  return inputMode;
};

export const createDefaultFoldingManager = () =>
  new FoldingManager(new Graph());

export const createDefaultNodeStyle = () => {
  return new RectangleNodeStyle({
    cornerSize: NodeDefaults.BORDER_RADIUS,
    fill: transparentize(NodeDefaults.TRANSPARENCY, colors.green600),
    stroke: Stroke.TRANSPARENT,
  });
};

const sanitizeColor = (color: string | null): string => {
  try {
    const lightness = parseToHsl(color || colors.green600).lightness;
    return lightness > NodeDefaults.MINIMUM_COLOR_LIGHTNESS
      ? color || colors.green600
      : setLightness(
          NodeDefaults.MINIMUM_COLOR_LIGHTNESS,
          color || colors.green600
        );
  } catch (error) {
    // Since the color string can theoretically be any value (e.g. '#ggrreey'),
    // the polished functions can crash.
    return setLightness(NodeDefaults.MINIMUM_COLOR_LIGHTNESS, colors.green600);
  }
};

export const getTransparentSimpleBlockDiagramColor = (
  color: string | null,
  lightenDarkColors?: boolean
) => {
  return transparentize(
    NodeDefaults.TRANSPARENCY,
    lightenDarkColors ? sanitizeColor(color) : color || colors.green600
  );
};

export const getHighlightedNodeStyle = (node: INode) => {
  const strokeFill = node.tag.color;
  return new RectangleNodeStyle({
    cornerSize: NodeDefaults.BORDER_RADIUS + NodeDefaults.DECORATION_MARGINS, // Since all indicators are rendered in front of the graph elements by
    // default, setting the fill to anything but null will block all clicks
    // for the collapse/expand icons.
    fill: null,
    stroke: new Stroke({
      fill: strokeFill,
      thickness: NodeDefaults.DECORATION_STROKE_THICKNESS,
    }),
  });
};

type CustomEdgeStyle = {
  color?: string | null;
  line?: string;
  lineBeginning?: ArrowType;
  lineEnding?: ArrowType;
  highlighted?: boolean;
};
const createCustomEdgeStyle = ({
  color,
  line,
  lineBeginning = ArrowType.NONE,
  lineEnding = ArrowType.DEFAULT,
}: CustomEdgeStyle) =>
  new PolylineEdgeStyle({
    smoothingLength: EdgeDefaults.SMOOTHING_LENGTH,
    stroke: new Stroke({
      fill: color ?? colors.grey950,
      thickness: 1,
      dashStyle: line,
    }),
    sourceArrow: new AqArrow(lineBeginning),
    targetArrow: new AqArrow(lineEnding),
  });

export const getHighlightedEdgeStyle = ({
  stroke,
  sourceArrow,
  targetArrow,
}: PolylineEdgeStyle) => {
  const { r, g, b } = (stroke?.fill as Color | null) || {
    r: 0,
    g: 0,
    b: 0,
  };

  const fill = `rgba(${r}, ${g}, ${b}, 0.5)`;
  const lineBeginning = sourceArrow
    ? (sourceArrow as AqArrow).arrowType
    : ArrowType.NONE;

  const lineEnding = targetArrow
    ? (targetArrow as AqArrow).arrowType
    : ArrowType.DEFAULT;

  return new PolylineEdgeStyle({
    smoothingLength: EdgeDefaults.SMOOTHING_LENGTH,
    stroke: new Stroke({ fill, thickness: 5 }),
    sourceArrow: new AqArrow(lineBeginning),
    targetArrow: new AqArrow(lineEnding),
  });
};

export const createDefaultEdgeStyle = () => createCustomEdgeStyle({});

export const createDefaultEdgeLabelLayoutParameter = () =>
  new EdgePathLabelModel({
    sideOfEdge: EdgeSides.ON_EDGE,
    autoRotation: false,
  }).createRatioParameter();

export const createDefaultLabelStyle = () =>
  new LabelStyle({ textSize: FontDefaults.FONT_SIZE });

const EXPANDER_PLACEMENT_MARGINS: InsetsConvertible = {
  top: -NodeDefaults.MARGIN - CollapseExpandButtonDefaults.HEIGHT,
  left: NodeDefaults.MARGIN,
};
const EXPANDER_SIZE: SizeConvertible = [
  CollapseExpandButtonDefaults.WIDTH,
  CollapseExpandButtonDefaults.HEIGHT,
];

export const createCollapsibleNodeStyleDecorator = (
  foldingView: IFoldingView
) => {
  const nodeStyleDecorator = new AqCollapsibleNodeStyleDecorator(
    foldingView.graph.groupNodeDefaults.style
  );
  nodeStyleDecorator.buttonPlacement = new ExteriorNodeLabelModel({
    margins: EXPANDER_PLACEMENT_MARGINS,
  }).createParameter(ExteriorNodeLabelModelPosition.TOP_LEFT);
  nodeStyleDecorator.buttonSize = EXPANDER_SIZE;
  return nodeStyleDecorator;
};

const nodeFillColor = (color: string | null, lightenDarkColors: boolean) =>
  getTransparentSimpleBlockDiagramColor(color, lightenDarkColors);

const createColorNodeStyle = (
  color: string | null,
  representationData: RepresentationData | null,
  iconColor: string,
  lightenDarkColors: boolean
) =>
  representationData
    ? new AqShapeAndIconNodeStyle(
        representationData,
        iconColor,
        nodeFillColor(color, lightenDarkColors)
      )
    : new RectangleNodeStyle({
        cornerSize: NodeDefaults.BORDER_RADIUS,
        fill: nodeFillColor(color, lightenDarkColors),
        stroke: Stroke.TRANSPARENT,
      });

type CustomGroupNodeStyle = {
  color: string | null;
  labelColor?: string;
  label?: string;
  representationData: RepresentationData | null;
  lightenDarkColors: boolean;
};
const createCustomGroupNodeStyle = ({
  color,
  labelColor,
  label,
  representationData,
  lightenDarkColors,
}: CustomGroupNodeStyle) => {
  const transparentizedColor = transparentize(
    GroupNodeDefaults.TRANSPARENCY,
    lightenDarkColors ? sanitizeColor(color) : color || colors.green600
  );
  const style = new AqGroupNodeStyle({
    color: transparentizedColor,
    representationData,
    iconColor: labelColor || colors.grey950,
    label: label || '',
    labelColor: labelColor || colors.grey950,
  });

  const nodeStyleDecorator = new AqCollapsibleNodeStyleDecorator(style);
  nodeStyleDecorator.buttonPlacement = new ExteriorNodeLabelModel({
    margins: EXPANDER_PLACEMENT_MARGINS,
  }).createParameter(ExteriorNodeLabelModelPosition.TOP_LEFT);
  nodeStyleDecorator.buttonSize = EXPANDER_SIZE;
  return nodeStyleDecorator;
};

const createColorLabelStyle = (color: string | null, cssClass?: string) =>
  new LabelStyle({
    font: NODE_FONT,
    textFill: color || colors.grey950,
    verticalTextAlignment: VerticalTextAlignment.CENTER,
    horizontalTextAlignment: HorizontalTextAlignment.CENTER,
    wrapping: TextWrapping.WRAP_WORD_ELLIPSIS,
    maximumSize: NODE_LABEL_SIZE,
    padding: 10,
    cssClass,
  });

type NodeWithExtraInfo = SimpleBlockDiagramNode & {
  children: ArdoqId[];
  iNode?: INode;
};

const isGroupNode = (node: NodeWithExtraInfo) => Boolean(node.children.length);

const getOrCreateINode = (
  graph: IGraph,
  nodeId: ArdoqId,
  nodes: { [s: string]: NodeWithExtraInfo },
  lightenDarkColors: boolean
): INode => {
  const node = nodes[nodeId];
  if (node.iNode) {
    return node.iNode;
  }
  const {
    label,
    labelColor,
    type,
    color,
    children,
    representationData,
    componentId,
    id,
  } = node;

  const tag = {
    elementType: type,
    elementId: componentId,
    id,
    color,
    text: label,
  };
  if (isGroupNode(node)) {
    const childINodes = children.map((childId: ArdoqId) =>
      getOrCreateINode(graph, childId, nodes, lightenDarkColors)
    );
    const iNode = graph.groupNodes(childINodes);
    graph.setStyle(
      iNode,
      createCustomGroupNodeStyle({
        color,
        label,
        labelColor,
        representationData,
        lightenDarkColors,
      })
    );
    iNode.tag = tag;
    node.iNode = iNode;
    return iNode;
  }
  const iconColor = labelColor ?? colors.grey950;
  const style = color
    ? createColorNodeStyle(
        color,
        representationData,
        iconColor,
        lightenDarkColors
      )
    : undefined;

  const iNode = graph.createNode({
    layout: new Rect(
      0,
      0,
      NodeDefaults.WIDTH,
      NodeDefaults.HEIGHT + NodeDefaults.MARGIN * 2
    ),
    style,
    tag,
  });

  const labelStyle = labelColor ? createColorLabelStyle(labelColor) : null;

  if (label) {
    graph.addLabel({
      owner: iNode,
      text: label,
      style: labelStyle ?? undefined,
    });
  }

  node.iNode = iNode;
  return iNode;
};

export const populateGraph = (
  graph: IGraph,
  nodes: SimpleBlockDiagramNode[],
  edges: SimpleBlockDiagramEdge[],
  lightenDarkColors: boolean
) => {
  const nodesWithExtraInfo: { [s: string]: NodeWithExtraInfo } = {};
  // Create copy of nodes, but with information about children and corresponding iNode
  nodes.forEach(node => {
    nodesWithExtraInfo[node.id] = Object.assign({}, node, {
      children: [],
      iNode: undefined,
    });
  });

  // Add info about children
  Object.values(nodesWithExtraInfo).forEach(node => {
    if (node.parentId) {
      if (nodesWithExtraInfo[node.parentId]) {
        nodesWithExtraInfo[node.parentId].children.push(node.id);
      }
    }
  });

  // Create nodes and groups
  Object.values(nodesWithExtraInfo).forEach(node => {
    getOrCreateINode(graph, node.id, nodesWithExtraInfo, lightenDarkColors);
  });

  // Create map from referenceId to IEdge
  edges.forEach(edge => {
    if (!(edge.sourceId && edge.targetId)) return;
    const {
      color,
      line,
      id,
      lineBeginning,
      lineEnding,
      label,
      referenceId,
      type,
      sourceId,
      targetId,
      referenceTypeName,
    } = edge;
    const sourceNode = nodesWithExtraInfo[sourceId];
    const targetNode = nodesWithExtraInfo[targetId];
    const sourceINode = sourceNode && sourceNode.iNode;
    const targetINode = targetNode && targetNode.iNode;
    const tag: IEdgeTag = {
      elementType: type,
      elementId: id,
      referenceId,
      color,
      text: label,
      referenceTypeName,
    };
    if (!sourceINode || !targetINode) return;

    const edgeStyle = createCustomEdgeStyle({
      color,
      line,
      lineBeginning,
      lineEnding,
    });
    const iEdge = graph.createEdge(sourceINode, targetINode, edgeStyle, tag);
    const labelStyle = createColorLabelStyle(
      color,
      EDGE_LABEL_CONTAINER_CLASS_NAME
    );
    if (!label) return;
    graph.addLabel({
      owner: iEdge,
      text: label,
      style: labelStyle,
      tag: { elementId: id },
    });
  });
};

// GraphWrapper utils
export const addGroupCollapsedListener = (
  component: GraphComponent,
  listener: (
    evt: InputModeItemEventArgs<INode>,
    sender: NavigationInputMode
  ) => void
) => {
  if (!(component.inputMode instanceof GraphViewerInputMode)) return;
  component.inputMode.navigationInputMode.addEventListener(
    'group-collapsed',
    listener
  );
};

export const removeGroupCollapsedListener = (
  component: GraphComponent,
  listener: (
    evt: InputModeItemEventArgs<INode>,
    sender: NavigationInputMode
  ) => void
) => {
  if (!(component.inputMode instanceof GraphViewerInputMode)) return;
  component.inputMode.navigationInputMode.removeEventListener(
    'group-collapsed',
    listener
  );
};

export const addGroupExpandedListener = (
  component: GraphComponent,
  listener: (
    evt: InputModeItemEventArgs<INode>,
    sender: NavigationInputMode
  ) => void
) => {
  if (!(component.inputMode instanceof GraphViewerInputMode)) return;
  component.inputMode.navigationInputMode.addEventListener(
    'group-expanded',
    listener
  );
};

export const removeGroupExpandedListener = (
  component: GraphComponent,
  listener: (
    evt: InputModeItemEventArgs<INode>,
    sender: NavigationInputMode
  ) => void
) => {
  if (!(component.inputMode instanceof GraphViewerInputMode)) return;
  component.inputMode.navigationInputMode.removeEventListener(
    'group-expanded',
    listener
  );
};

export const removeHoveredItemChangedListener = (
  component: GraphComponent,
  listener: (
    evt: HoveredItemChangedEventArgs,
    sender: ItemHoverInputMode
  ) => void
) => {
  if (!(component.inputMode instanceof GraphViewerInputMode)) return;
  component.inputMode.itemHoverInputMode.removeEventListener(
    'hovered-item-changed',
    listener
  );
};

export const addHoveredItemChangedListener = (
  component: GraphComponent,
  listener: (
    evt: HoveredItemChangedEventArgs,
    sender: ItemHoverInputMode
  ) => void
) => {
  if (!(component.inputMode instanceof GraphViewerInputMode)) return;
  component.inputMode.itemHoverInputMode.addEventListener(
    'hovered-item-changed',
    listener
  );
};

export const removeItemClickedListener = (
  component: GraphComponent,
  listener: (
    evt: ItemClickedEventArgs<IModelItem>,
    sender: GraphViewerInputMode
  ) => void
) => {
  if (!(component.inputMode instanceof GraphViewerInputMode)) return;
  component.inputMode.removeEventListener('item-clicked', listener);
};

export const addItemClickedListener = (
  component: GraphComponent,
  listener: (
    evt: ItemClickedEventArgs<IModelItem>,
    sender: GraphViewerInputMode
  ) => void
) => {
  if (!(component.inputMode instanceof GraphViewerInputMode)) return;
  component.inputMode.addEventListener('item-clicked', listener);
};

export const removeItemDoubleClickedListener = (
  component: GraphComponent,
  listener: (
    evt: ItemClickedEventArgs<IModelItem>,
    sender: GraphViewerInputMode
  ) => void
) => {
  if (!(component.inputMode instanceof GraphViewerInputMode)) return;
  component.inputMode.removeEventListener('item-double-clicked', listener);
};

export const addItemDoubleClickedListener = (
  component: GraphComponent,
  listener: (
    evt: ItemClickedEventArgs<IModelItem>,
    sender: GraphViewerInputMode
  ) => void
) => {
  if (!(component.inputMode instanceof GraphViewerInputMode)) return;
  component.inputMode.addEventListener('item-double-clicked', listener);
};

export const removeCanvasClickedListener = (
  component: GraphComponent,
  listener: (evt: ClickEventArgs, sender: GraphViewerInputMode) => void
) => {
  if (!(component.inputMode instanceof GraphViewerInputMode)) return;
  component.inputMode.removeEventListener('canvas-clicked', listener);
};

export const addCanvasClickedListener = (
  component: GraphComponent,
  listener: (evt: ClickEventArgs, sender: GraphViewerInputMode) => void
) => {
  if (!(component.inputMode instanceof GraphViewerInputMode)) return;
  component.inputMode.addEventListener('canvas-clicked', listener);
};

const getDefaultNodeLayout = (node: INode) =>
  Rect.fromCenter(
    node.layout.center,
    new Size(NodeDefaults.WIDTH, NodeDefaults.HEIGHT)
  ); // Make sure the collapsing node minimizes towards its center
export const resetNodeToDefaultCollapsedSize = (graph: IGraph, node: INode) =>
  graph.setNodeLayout(node, getDefaultNodeLayout(node));

const getLayoutOptions = (
  layoutOrientation: LayoutOrientation
): ConstructorParameters<typeof HierarchicalLayout>[0] => ({
  /**
   * gridSpacing is the spacing between the invisible lines yFiles aligns things
   * after, so gridSpacing: 2 * NodeDefaults.MARGIN + FontDefaults.LINE_HEIGHT
   * ensures that the height between the top of a child node and the the top of
   * its parent node is always large enough to fit the icon with margin above
   * and below.
   */
  gridSpacing: 2 * NodeDefaults.MARGIN + FontDefaults.LINE_HEIGHT,
  minimumLayerDistance: EdgeDefaults.MINIMUM_LAYER_DISTANCE,
  defaultEdgeDescriptor: {
    routingStyleDescriptor: {
      defaultRoutingStyle: HierarchicalLayoutRoutingStyle.ORTHOGONAL,
    },
  },
  edgeLabelPlacement: 'integrated',
  layoutOrientation,
});

const getLayoutData = (graph: IGraph, edgeGrouping: boolean) => {
  const layoutData = new HierarchicalLayoutData({ edgeDirectedness: 1 });

  if (edgeGrouping) {
    const { edgeSourceGroup, edgeTargetGroup } = groupEdges(graph);
    layoutData.sourceGroupIds = edgeSourceGroup;
    layoutData.targetGroupIds = edgeTargetGroup;

    const edgeLabelPreferredPlacement = processDuplicateLabels(graph);
    layoutData.edgeLabelPreferredPlacements = (label: ILabel) =>
      edgeLabelPreferredPlacement.get(label) ?? DEFAULT_PLACEMENT_DESCRIPTOR;
  }

  return layoutData;
};

export const calculateAndApplyInitialHierarchicLayout = (
  graphComponent: GraphComponent,
  layoutOrientation: LayoutOrientation,
  edgeGrouping: boolean,
  shouldAnimate: boolean
) => {
  const layout = new HierarchicalLayout(getLayoutOptions(layoutOrientation));
  const layoutData = getLayoutData(graphComponent.graph, edgeGrouping);

  if (shouldAnimate) {
    graphComponent.applyLayoutAnimated(
      layout,
      ANIMATION_DURATION_TIME_SPAN,
      layoutData
    );
    return;
  }

  graphComponent.graph.applyLayout(layout, layoutData);
  graphComponent.updateContentBounds();
};

export const centerViewportOnFocusedItem = (
  graphComponent: GraphComponent,
  focusedItemId: string
) => {
  const node = graphComponent.graph.nodes.find(node => {
    return node.tag.elementId === focusedItemId;
  });
  if (node === null) return;
  graphComponent.center = node.layout.center;
  graphComponent.zoom = 0.5;
};

type IncrementalHierarchicLayoutArgs = {
  graphComponent: GraphComponent;
  layoutOrientation: LayoutOrientation;
  edgeGrouping: boolean;
  edges?: SimpleBlockDiagramEdge[];
  getEdgeCountMap?: Map<IEdge, number>;
  updateContentBounds?: boolean;
  animateViewport?: boolean;
  onLayoutApplied?: VoidFunction;
  zoomToNodes?: INode[];
};
export const calculateAndApplyIncrementalHierarchicLayout = ({
  graphComponent,
  layoutOrientation,
  edgeGrouping,
  edges,
  getEdgeCountMap,
  updateContentBounds,
  animateViewport = true,
  onLayoutApplied,
  zoomToNodes,
}: IncrementalHierarchicLayoutArgs) => {
  const layout = new HierarchicalLayout({
    ...getLayoutOptions(layoutOrientation),
    fromSketchMode: true,
  });
  const layoutData = getLayoutData(graphComponent.graph, edgeGrouping);
  if (getEdgeCountMap && edges && !edgeGrouping) {
    updateEdgeLabels(graphComponent.graph, edges, getEdgeCountMap);
  }
  const options = {
    graphComponent,
    layout,
    layoutData,
    animationDuration: ANIMATION_DURATION_TIME_SPAN,
    animateViewport,
    updateContentBounds,
  };

  const layoutExecutor = zoomToNodes?.length
    ? new ZoomToNodesLayoutExecutor(zoomToNodes, options)
    : new LayoutExecutor(options);
  layoutExecutor.start().then(() => {
    onLayoutApplied?.();
  });
};

export const getGraphItemsById = (graph: IGraph, id: string) => {
  const graphItems = [...graph.nodes, ...graph.edges, ...graph.labels].filter(
    item => item.tag && item.tag.elementId === id
  );
  return graphItems ?? null;
};

export const selectGraphItemsById = (
  graphComponent: GraphComponent,
  id: string | null
) => {
  if (!graphComponent.inputMode) return;
  const graphItems =
    id !== null ? getGraphItemsById(graphComponent.graph, id) : null;
  graphItems?.forEach(graphItem => {
    if (!(graphComponent.inputMode instanceof GraphViewerInputMode)) return;
    graphComponent.inputMode.setSelected(graphItem, true);
  });
};

export const layoutOrientationMap = new Map([
  ['left-to-right', LayoutOrientation.LEFT_TO_RIGHT],
  ['right-to-left', LayoutOrientation.RIGHT_TO_LEFT],
  ['top-to-bottom', LayoutOrientation.TOP_TO_BOTTOM],
  ['bottom-to-top', LayoutOrientation.BOTTOM_TO_TOP],
]);

const getViewGroups = (graph: IGraph | null) => {
  const allMasterGroups = getAllMasterGroups(graph);
  if (allMasterGroups?.size && graph?.foldingView) {
    return allMasterGroups
      .map(masterNode => graph.foldingView!.getViewItem(masterNode))
      .filter(Boolean) as IEnumerable<INode>;
  }
};

const getAllMasterGroups = (graph: IGraph | null) => {
  const viewManager = graph?.foldingView?.manager;
  return viewManager?.masterGraph.nodes.filter(node =>
    viewManager.masterGraph.isGroupNode(node)
  );
};

export const getCollapsedViewGroups = (graph: IGraph | null) => {
  if (!graph?.foldingView) {
    return null;
  }
  return getViewGroups(graph)?.filter(
    node => !graph.foldingView!.isExpanded(node)
  );
};

type CollapseAllGroupsArgs = {
  graph: IGraph | null;
  graphComponent: GraphComponent | null;
  layoutOrientation: LayoutOrientation;
  edgeGrouping: boolean;
  filterEdgeDuplicates: VoidFunction;
};
export const collapseAllGroups = ({
  graph,
  graphComponent,
  layoutOrientation,
  edgeGrouping,
  filterEdgeDuplicates,
}: CollapseAllGroupsArgs) => {
  if (!graph?.foldingView || !graphComponent) {
    return;
  }
  const allMasterGroups = getAllMasterGroups(graph);
  if (!allMasterGroups?.size) {
    return;
  }

  const viewManager = graph?.foldingView?.manager;

  allMasterGroups.forEach(masterNode => {
    const viewItem = graph.foldingView!.getViewItem(masterNode);
    const folderState = viewManager?.getFolderNodeState(masterNode);
    if (folderState && viewItem) {
      const newLayout = getDefaultNodeLayout(viewItem);
      folderState.layout = newLayout;
      viewManager?.updateFolderNodeState(masterNode);
    }
  });

  getViewGroups(graph)?.forEach(node => graph.foldingView?.collapse(node));
  filterEdgeDuplicates();
  calculateAndApplyIncrementalHierarchicLayout({
    graphComponent,
    layoutOrientation,
    edgeGrouping,
  });
};

type ExpandAllGroupsArgs = {
  graph: IGraph | null;
  graphComponent: GraphComponent | null;
  viewGroups?: IEnumerable<INode> | null;
  layoutOrientation: LayoutOrientation;
  edgeGrouping: boolean;
  filterEdgeDuplicates: VoidFunction;
};
export const expandAllGroups = (args: ExpandAllGroupsArgs) => {
  const {
    graph,
    graphComponent,
    viewGroups,
    layoutOrientation,
    edgeGrouping,
    filterEdgeDuplicates,
  } = args;

  if (!graph?.foldingView || !graphComponent) {
    return;
  }

  viewGroups?.forEach(node => graph.foldingView?.expand(node));

  const collapsedGroups = getCollapsedViewGroups(graph);

  if (collapsedGroups && collapsedGroups.size !== 0) {
    expandAllGroups({
      ...args,
      viewGroups: collapsedGroups,
    });
  } else {
    filterEdgeDuplicates();
    calculateAndApplyIncrementalHierarchicLayout({
      graphComponent,
      layoutOrientation,
      edgeGrouping,
    });
  }
};

const getIsCollapseAllActiveForSimpleBlockDiagram = (
  graph: IGraph | null,
  hasNoGroups: boolean
) =>
  !hasNoGroups &&
  getCollapsedViewGroups(graph)?.size === getViewGroups(graph)?.size;

const getIsExpandAllActiveForSimpleBlockDiagram = (
  graph: IGraph | null,
  hasNoGroups: boolean
) => !hasNoGroups && getCollapsedViewGroups(graph)?.size === 0;

type GetLeftMenuConfigForExpandCollapseAllArgs = {
  isCollapseAllActive: boolean;
  onClickCollapseAll: VoidFunction;
  isExpandAllActive: boolean;
  onClickExpandAll: VoidFunction;
  isCollapseAllDisabled: boolean;
  isExpandAllDisabled: boolean;
  collapseAllLabel?: string;
  expandAllLabel?: string;
};
const getLeftMenuConfigForExpandCollapseAll = ({
  isCollapseAllActive,
  onClickCollapseAll,
  isExpandAllActive,
  onClickExpandAll,
  isCollapseAllDisabled,
  isExpandAllDisabled,
  collapseAllLabel = 'Collapse all',
  expandAllLabel = 'Expand all',
}: GetLeftMenuConfigForExpandCollapseAllArgs) => [
  {
    id: 'CollapseAll',
    label: collapseAllLabel,
    iconName: IconName.COLLAPSE_ALL,
    isDisabled: isCollapseAllDisabled,
    isActive: isCollapseAllActive,
    onClick: onClickCollapseAll,
    type: SettingsType.BUTTON,
  },
  {
    id: 'ExpandAll',
    label: expandAllLabel,
    iconName: IconName.EXPAND_ALL,
    isDisabled: isExpandAllDisabled,
    isActive: isExpandAllActive,
    onClick: onClickExpandAll,
    type: SettingsType.BUTTON,
  },
];

type TranslateGraphPropsToButtonConfigPropsArgs = {
  graph: IGraph | null;
  graphComponent: GraphComponent | null;
  layoutOrientation: LayoutOrientation;
  edgeGrouping: boolean;
  edges: SimpleBlockDiagramEdge[];
  filterEdgeDuplicates: VoidFunction;
  collapseAllLabel?: string;
  expandAllLabel?: string;
};
export const translateGraphPropsToButtonConfigProps = (
  graphProps: TranslateGraphPropsToButtonConfigPropsArgs
): ButtonConfig[] => {
  const { graph, collapseAllLabel, expandAllLabel } = graphProps;

  const onClickCollapseAll = () => collapseAllGroups(graphProps);
  const onClickExpandAll = () =>
    expandAllGroups({
      ...graphProps,
      viewGroups: getCollapsedViewGroups(graph),
    });

  const allMasterGroups = getAllMasterGroups(graph);
  const hasNoGroups = !allMasterGroups || allMasterGroups.size === 0;

  return getLeftMenuConfigForExpandCollapseAll({
    isCollapseAllActive: getIsCollapseAllActiveForSimpleBlockDiagram(
      graph,
      hasNoGroups
    ),
    onClickCollapseAll,
    isExpandAllActive: getIsExpandAllActiveForSimpleBlockDiagram(
      graph,
      hasNoGroups
    ),
    onClickExpandAll,
    isCollapseAllDisabled: hasNoGroups,
    isExpandAllDisabled: hasNoGroups,
    collapseAllLabel,
    expandAllLabel,
  });
};

/** Set correct edge labels after edge grouping change */
export const updateEdgeLabels = (
  graph: IGraph,
  edges: SimpleBlockDiagramEdge[],
  edgeCountMap: Map<IEdge, number>
) => {
  const initialLabelTextByEdgeId: Record<string, string> = edges.reduce(
    (acc, edge) => (edge?.label ? { ...acc, [edge.id]: edge?.label } : acc),
    {}
  );

  graph.edges.forEach(edge => {
    edge.labels.toList().forEach(label => {
      // Remove existing count labels
      if (label.tag?.countLabel) {
        graph.remove(label);
        return;
      }

      const initialLabelText =
        edge.tag?.elementId && initialLabelTextByEdgeId[edge.tag?.elementId];

      if (initialLabelText && label && initialLabelText !== label.text) {
        graph.setLabelText(label, initialLabelText);
      }
    });

    const edgesCount = edgeCountMap.get(edge);
    if (edgesCount && edgesCount > 1) {
      graph.addLabel({
        owner: edge,
        text: edgesCount.toString(),
        style: countLabelStyle,
        layoutParameter: edgeCountLabelParameter,
        tag: { elementId: edge.tag.elementId, countLabel: true },
      });
    }
  });
};

export const getEdgeCountMap = (edges: Iterable<IEdge>): Map<IEdge, number> => {
  const edgeDuplicatesCountMap: Map<string, { edge: IEdge; count: number }> =
    new Map();

  for (const edge of edges) {
    const { sourceNode, targetNode, tag } = edge;
    const key = `${sourceNode?.tag.id}-${targetNode?.tag.id}-${tag?.text}-${tag?.referenceTypeName}`;
    const duplicateEdge = edgeDuplicatesCountMap.get(key);

    if (duplicateEdge) {
      duplicateEdge.count += 1;
    } else {
      edgeDuplicatesCountMap.set(key, { edge, count: 1 });
    }
  }

  return new Map(
    [...edgeDuplicatesCountMap.values()].map(({ edge, count }) => [edge, count])
  );
};
